import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { getV2ApiCompanyIdAccountingIntegrationsSyncOperationsSyncMode } from 'src/client/spendesk-api';
import { type GetV2ApiCompanyIdAccountingIntegrationsSyncOperationsSyncModeResponseOK } from 'src/client/spendesk-api-types';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';

import { useInvalidateAnalyticalFieldMappingQuery } from '../../bookkeep/settings/integrations/hooks/useAnalyticalFieldMappingsQuery';
import { useHasMapSync } from '../hooks/useHasMapSync';

export type SyncOperation =
  | GetV2ApiCompanyIdAccountingIntegrationsSyncOperationsSyncModeResponseOK
  | null
  | undefined;

export const useGetSyncOperationStatus = (
  syncMode: 'pull' | 'push',
  customFieldId?: string,
) => {
  const companyId = useCompanyId();
  const isEnabled = useHasMapSync();

  const [refetchInterval, setRefetchInterval] = useState<number>();
  const queryClient = useQueryClient();
  const invalidateMappingQuery = useInvalidateAnalyticalFieldMappingQuery();

  const { isLoading, data } = useQuery({
    queryKey: ['sync-operations', companyId],
    refetchInterval,
    refetchOnWindowFocus: true,
    enabled: isEnabled,
    staleTime: 5000,
    queryFn: async () => {
      const response =
        await getV2ApiCompanyIdAccountingIntegrationsSyncOperationsSyncMode({
          path: {
            companyId,
            syncMode,
          },
        });

      return response.statusCode === 200 ? response.body : null;
    },
    onSuccess(successData): void {
      if (!successData) {
        return;
      }

      if (successData.status === 'in_progress') {
        setRefetchInterval(5000);
        return;
      }

      setRefetchInterval(undefined);

      if (successData.status === 'succeeded') {
        invalidateMappingQuery();
        queryClient.invalidateQueries({
          queryKey: 'expenseCategories',
        });

        if (customFieldId) {
          queryClient.invalidateQueries({
            queryKey: ['useCustomFieldValuesQuery', customFieldId],
          });
        }
      }
    },
  });

  return {
    isLoading,
    isSyncOperationInProgress: data?.status === 'in_progress',
    syncOperation: data,
  };
};

export const useInvalidateSyncOperationQuery = () => {
  const queryClient = useQueryClient();
  const companyId = useCompanyId();
  return () => {
    queryClient.invalidateQueries({
      queryKey: ['sync-operations', companyId],
    });
  };
};
